import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box } from '@mui/material';
import { useDataProvider, useGetIdentity, useRefresh } from 'react-admin';
import { DEFAULTS } from '../../constants';
import { toast } from 'react-toastify';
import { CreateDivisionDialogProps } from '../../utils/DataTypes';

const CreateDivisionDialog: React.FC<CreateDivisionDialogProps> = ({ isOpen, onClose, onSave, selectedClient }) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [divisionNumber, setDivisionNumber] = useState('');
  const [divisionName, setDivisionName] = useState('');
  const [divisionDisplayNumber, setDivisionDisplayNumber] = useState('');
  const [isExistingDivision, setIsExistingDivision] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const errorMsg = isExistingDivision
    ? `Division ${divisionNumber.trim()} already exists for this client. Please edit the existing record instead.`
    : '';

  const handleValidateDivisionNumber = async () => {
    setIsExistingDivision(false);
    const cleanNumber = divisionNumber.trim();

    if (!selectedClient || !cleanNumber) return false;

    setIsValidating(true);
    try {
      const { data: duplicateData } = await dataProvider.getList('ValidateDivisions', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: selectedClient.billing_client_number,
          division_numbers: [cleanNumber],
        },
      });

      const found = duplicateData.length > 0;
      setIsExistingDivision(found);
      return found;
    } catch (error) {
      toast.error('Division validation failed');
      return true;
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    const duplicateFound = await handleValidateDivisionNumber();
    if (duplicateFound) return;

    const cleanNumber = divisionNumber.trim();
    const cleanName = divisionName.trim();
    const cleanDisplay = divisionDisplayNumber.trim();

    const payload = {
      import_mode: 'append',
      billing_client_name: selectedClient?.billing_client_name || '',
      billing_client_number: selectedClient?.billing_client_number || '',
      divisions: [
        {
          division_number: cleanNumber,
          division_name: cleanName,
          division_display_number: cleanDisplay,
          created_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
          last_modified_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        },
      ],
    };

    try {
      const { data: result } = await dataProvider.create('BulkAddDivisions', {
        data: payload,
      });

      toast.success(result.message || 'Division created successfully!');
      refresh();
      onClose();
      onSave(payload);
    } catch (error: any) {
      toast.error(`Create failed: ${error.message || 'Server error'}`);
    }
  };

  const isSaveDisabled =
    !selectedClient || !divisionNumber.trim() || !divisionName.trim() || isExistingDivision || isValidating;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create New Division Crosswalk</DialogTitle>
      <DialogContent>
        <Box display="flex" gap={2} mb={1} mt={1}>
          <TextField
            label="Billing Client Name"
            value={selectedClient?.billing_client_name || ''}
            fullWidth
            variant="standard"
            disabled
          />
          <TextField
            label="Client Number"
            value={selectedClient?.billing_client_number || ''}
            variant="standard"
            disabled
            sx={{ width: '200px' }}
          />
        </Box>

        <TextField
          required
          margin="dense"
          label="Division Number"
          type="text"
          fullWidth
          variant="standard"
          value={divisionNumber}
          error={isExistingDivision}
          helperText={errorMsg}
          onChange={(e) => {
            setDivisionNumber(e.target.value);
            setIsExistingDivision(false);
          }}
          onBlur={handleValidateDivisionNumber}
        />
        <TextField
          margin="dense"
          label="Display Number"
          type="text"
          fullWidth
          variant="standard"
          value={divisionDisplayNumber}
          onChange={(e) => setDivisionDisplayNumber(e.target.value)}
        />
        <TextField
          required
          margin="dense"
          label="Division Name"
          type="text"
          fullWidth
          variant="standard"
          value={divisionName}
          onChange={(e) => setDivisionName(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isValidating}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaveDisabled}>
          {isValidating ? 'Validating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDivisionDialog;
